/**
 * Profile Page - User profile with parents and players
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { AdminPageLayout, Section, Card, Badge, Modal } from '../components';
import { AttributeStack, AttributeGrid } from '../components/admin/patterns/AttributePatterns';
import ProfileInfoCard from '../components/ProfileInfoCard';
import Button from '../components/Button';
import LoadingState from '../components/LoadingState';
import EmergencyContactEditor from '../components/EmergencyContactEditor';
import ParentEditForm from '../components/ParentEditForm';
import PlayerEditForm from '../components/PlayerEditForm';
import { useAuth } from '../contexts/AuthContext';
import { useRole } from '../contexts/RoleContext';
import { useToast } from '../contexts/ToastContext';
import apiService from '../services/api';
import {
  Icon, CheckCircle,
  AlertTriangle, Edit, User, Mail, ShieldCheck, Calendar, Clock, Plus
} from 'lucide-react';
import { soccerBall } from '@lucide/lab';



// Club Admin Profile Component
const ClubAdminProfile = () => {
  const { user } = useAuth();

  return (
    <AdminPageLayout
      title="CLUB ADMIN PROFILE"
      subtitle="Club administrator account information"
      maxWidth="1400px"
      backButton={false}
    >
      <Section title="ACCOUNT INFORMATION">
        <Card level={1}>
          <AttributeGrid columns="auto-fit" minWidth="250px" gap="2.5rem">
            <AttributeStack label="Name" value={`${user?.first_name || ''} ${user?.last_name || ''}`} icon={<User size={16} />} />
            <AttributeStack label="Email" value={user?.email || 'Not provided'} icon={<Mail size={16} />} />
            <AttributeStack label="Role" value={<Badge variant="primary">CLUB ADMIN</Badge>} icon={<ShieldCheck size={16} />} />
            <AttributeStack label="Status" value={user?.status?.toUpperCase() || 'ACTIVE'} />
            <AttributeStack label="Account Created" value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'} icon={<Calendar size={16} />} />
            <AttributeStack label="Last Updated" value={user?.updated_at ? new Date(user.updated_at).toLocaleDateString() : 'N/A'} icon={<Clock size={16} />} />
          </AttributeGrid>
        </Card>
      </Section>
    </AdminPageLayout>
  );
};


// Platform Admin Profile Component
const PlatformAdminProfile = () => {
  const { user } = useAuth();

  return (
    <AdminPageLayout
      title="PLATFORM ADMIN PROFILE"
      subtitle="System administrator account information"
      maxWidth="1400px"
      backButton={false}
    >
      <Section title="ACCOUNT INFORMATION">
        <Card level={1}>
          <AttributeGrid columns="auto-fit" minWidth="250px" gap="2.5rem">
            <AttributeStack label="Name" value={`${user?.first_name || ''} ${user?.last_name || ''}`} icon={<User size={16} />} />
            <AttributeStack label="Email" value={user?.email || 'Not provided'} icon={<Mail size={16} />} />
            <AttributeStack label="Role" value={<Badge variant="error">PLATFORM ADMIN</Badge>} icon={<ShieldCheck size={16} />} />
            <AttributeStack label="Status" value={user?.status?.toUpperCase() || 'ACTIVE'} />
            <AttributeStack label="Account Created" value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'} icon={<Calendar size={16} />} />
            <AttributeStack label="Last Updated" value={user?.updated_at ? new Date(user.updated_at).toLocaleDateString() : 'N/A'} icon={<Clock size={16} />} />
          </AttributeGrid>
        </Card>
      </Section>

      <Section title="SYSTEM ACCESS & PERMISSIONS">
        <Card level={1}>
          <div style={{ padding: '1.5rem' }}>
            <h4 style={{ color: 'var(--color-primary)', marginBottom: '1.5rem', fontSize: '1.1rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Platform Administrator Privileges
            </h4>
            <div style={{ display: 'grid', gap: '1rem' }}>
              {[
                'Create and manage soccer clubs',
                'Automatically generate club admin users',
                'Monitor platform-wide activities',
                'Access system configuration',
                'View all organizational data',
                'Multi-tenant management capabilities'
              ].map((privilege, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <CheckCircle size={18} style={{ color: 'var(--color-success)', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.95rem', color: 'var(--color-text-primary)' }}>{privilege}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </Section>
    </AdminPageLayout>
  );
};

// Branch Admin Profile Component
const BranchAdminProfile = () => {
  const { user } = useAuth();

  return (
    <AdminPageLayout
      title="BRANCH ADMIN PROFILE"
      subtitle="Branch administrator account information"
      maxWidth="1400px"
      backButton={false}
    >
      <Section title="ACCOUNT INFORMATION">
        <Card level={1}>
          <AttributeGrid columns="auto-fit" minWidth="250px" gap="2.5rem">
            <AttributeStack label="Name" value={`${user?.first_name || ''} ${user?.last_name || ''}`} icon={<User size={16} />} />
            <AttributeStack label="Email" value={user?.email || 'Not provided'} icon={<Mail size={16} />} />
            <AttributeStack label="Role" value={<Badge variant="primary">BRANCH ADMIN</Badge>} icon={<ShieldCheck size={16} />} />
            <AttributeStack label="Status" value={user?.status?.toUpperCase() || 'ACTIVE'} />
            <AttributeStack label="Account Created" value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'} icon={<Calendar size={16} />} />
            <AttributeStack label="Last Updated" value={user?.updated_at ? new Date(user.updated_at).toLocaleDateString() : 'N/A'} icon={<Clock size={16} />} />
          </AttributeGrid>
        </Card>
      </Section>
    </AdminPageLayout>
  );
};

// Coach Profile Component
const CoachProfile = () => {
  const { user } = useAuth();
  const [editingCertifications, setEditingCertifications] = React.useState(false);

  return (
    <AdminPageLayout
      title="COACH PROFILE"
      subtitle="Coach account and professional information"
      maxWidth="1400px"
      backButton={false}
    >
      <Section title="ACCOUNT INFORMATION">
        <Card level={1}>
          <AttributeGrid columns="auto-fit" minWidth="250px" gap="2.5rem">
            <AttributeStack label="Name" value={`${user?.first_name || ''} ${user?.last_name || ''}`} icon={<User size={16} />} />
            <AttributeStack label="Email" value={user?.email || 'Not provided'} icon={<Mail size={16} />} />
            <AttributeStack label="Role" value={<Badge variant="primary">COACH</Badge>} icon={<ShieldCheck size={16} />} />
            <AttributeStack label="Status" value={user?.status?.toUpperCase() || 'ACTIVE'} />
            <AttributeStack label="Account Created" value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'} icon={<Calendar size={16} />} />
            <AttributeStack label="Last Updated" value={user?.updated_at ? new Date(user.updated_at).toLocaleDateString() : 'N/A'} icon={<Clock size={16} />} />
          </AttributeGrid>
        </Card>
      </Section>

      <Section title="PROFESSIONAL INFORMATION">
        <Card level={1}>
          <AttributeGrid columns="auto-fit" minWidth="250px" gap="2.5rem">
            <AttributeStack 
              label="Experience Level" 
              value={user?.coach_experience_level || 'Not provided'} 
            />
            <AttributeStack 
              label="Certifications" 
              value={user?.coach_certifications || 'Not provided'} 
            />
            <AttributeStack 
              label="Specialty Areas" 
              value={user?.coach_specialties || 'Not provided'} 
            />
            <AttributeStack 
              label="Bio" 
              value={user?.coach_bio || 'Not provided'} 
            />
          </AttributeGrid>
        </Card>
      </Section>

      <Section title="COACHING OVERVIEW">
        <Card level={1}>
          <AttributeGrid columns="auto-fit" minWidth="250px" gap="2.5rem">
            <AttributeStack 
              label="Teams Coached" 
              value={user?.teams_coached_count || 0} 
            />
            <AttributeStack 
              label="Players Assessed" 
              value={user?.players_assessed_count || 0} 
            />
            <AttributeStack 
              label="Session Templates" 
              value={user?.session_templates_count || 0} 
            />
            <AttributeStack 
              label="Branch ID" 
              value={user?.branch_id || user?.location_id || 'Not assigned'} 
            />
          </AttributeGrid>
        </Card>
      </Section>
    </AdminPageLayout>
  );
};

// Parent/Family Profile Component - For Parent role ONLY
const ParentProfile = () => {
  const { user, isAuthenticated, loading: authLoading, getFamilyInfo, isRegistrationComplete } = useAuth();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const [familyData, setFamilyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingParent, setEditingParent] = useState(null);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [addingParent, setAddingParent] = useState(false);
  const [addingPlayer, setAddingPlayer] = useState(false);
  const [editingEmergencyContacts, setEditingEmergencyContacts] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadFamilyData = async () => {
      try {
        const data = await getFamilyInfo();
        setFamilyData(data);
      } catch (error) {
        console.error('Failed to load family data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading && isAuthenticated) {
      loadFamilyData();
    }
  }, [isAuthenticated, authLoading, getFamilyInfo]);

  // Redirect if not authenticated (but only after auth check is complete)
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/auth');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Show loading while auth is being checked
  if (authLoading) {
    return (
      <PageLayout>
        <LoadingState />
      </PageLayout>
    );
  }

  // Don't render if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  const registrationComplete = isRegistrationComplete();
  const hasParents = familyData?.parents?.length > 0;
  const hasPlayers = familyData?.players?.length > 0;
  const parentsCount = familyData?.parents?.length || 0;

  // Domain rules enforcement
  const canAddParent = parentsCount < 2; // Maximum 2 parents
  const hasEmergencyContact = familyData?.emergency_contacts && Object.keys(familyData.emergency_contacts).length > 0;

  const handleSaveParent = async (parentData) => {
    setSaving(true);

    try {
      const response = await apiService.updateParentInfo(editingParent.id, parentData);

      if (response.success) {
        showSuccess(
          response.email_changed
            ? 'Parent information updated successfully. Email notifications have been sent.'
            : 'Parent information updated successfully.'
        );

        // Reload family data
        const updatedData = await getFamilyInfo();
        setFamilyData(updatedData);
        setEditingParent(null);
      }
    } catch (error) {
      showError(error.message || 'Failed to update parent information');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePlayer = async (playerData) => {
    setSaving(true);

    try {
      const response = await apiService.updatePlayerInfo(editingPlayer.id, playerData);

      if (response.success) {
        showSuccess('Player information updated successfully.');

        // Reload family data
        const updatedData = await getFamilyInfo();
        setFamilyData(updatedData);
        setEditingPlayer(null);
      }
    } catch (error) {
      showError(error.message || 'Failed to update player information');
    } finally {
      setSaving(false);
    }
  };

  const handleAddParent = async (parentData) => {
    setSaving(true);

    try {
      const response = await apiService.addParent(parentData);

      if (response.success) {
        showSuccess('Parent added successfully.');

        // Reload family data
        const updatedData = await getFamilyInfo();
        setFamilyData(updatedData);
        setAddingParent(false);
      }
    } catch (error) {
      showError(error.message || 'Failed to add parent. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddPlayer = async (playerData) => {
    setSaving(true);

    try {
      // First, check if addPlayer endpoint exists in API
      // If not, we need to navigate to registration or show an error
      const response = await apiService.addPlayer(playerData);

      if (response.success) {
        showSuccess('Player added successfully.');

        // Reload family data
        const updatedData = await getFamilyInfo();
        setFamilyData(updatedData);
        setAddingPlayer(false);
      }
    } catch (error) {
      showError(error.message || 'Failed to add player. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEmergencyContacts = async (parentId, emergencyData) => {
    try {
      console.log('Saving emergency contacts for parent:', parentId, emergencyData);
      const response = await apiService.updateParentInfo(parentId, {
        emergency_contacts: emergencyData.emergency_contacts
      });

      if (response.success) {
        console.log('Successfully saved emergency contacts for parent:', parentId);
        return response;
      }
    } catch (error) {
      console.error('Failed to save emergency contacts for parent:', parentId, error);
      throw error;
    }
  };

  // This wrapper is called by EmergencyContactEditor with the consolidated contact list
  const handleEmergencyContactsSave = async (consolidatedContacts) => {
    setSaving(true);

    try {
      console.log('Starting emergency contacts save with list:', consolidatedContacts);

      // Find primary parent
      const primaryParent = familyData?.parents?.find(p => p.family_role === 'primary_parent');

      if (!primaryParent) {
        throw new Error('Primary parent not found. Cannot save emergency contacts.');
      }

      const primaryParentId = primaryParent.id;

      console.log(`Saving emergency contacts for primary parent ${primaryParentId}`);

      // Save ONLY for the primary parent with the consolidated list
      // Note: We wrap it in an object to match the API expectation { emergency_contacts: [...] }
      await handleSaveEmergencyContacts(primaryParentId, { emergency_contacts: consolidatedContacts });

      console.log('Successfully saved emergency contacts');

      showSuccess('Emergency contacts saved successfully');

      // Reload family data
      const updatedData = await getFamilyInfo();
      setFamilyData(updatedData);
      setEditingEmergencyContacts(false);
    } catch (error) {
      console.error('Error in handleEmergencyContactsSave:', error);
      showError(error.message || 'Failed to update emergency contacts');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout>
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto',
        padding: '2rem 1rem'
      }}>
        <div style={{
          marginBottom: '2rem',
          paddingBottom: '1.5rem',
          borderBottom: '2px solid var(--color-primary)'
        }}>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'var(--color-primary)',
            margin: '0 0 0.5rem 0'
          }}>PARENT PROFILE</h1>
          <p style={{
            fontSize: '1rem',
            color: 'var(--color-text-secondary)',
            margin: 0
          }}>Manage your family information and player registrations</p>
        </div>

        {/* Domain Rules Info Banner */}
        {!registrationComplete && (
          <ProfileInfoCard
            title="Profile Requirements"
            style={{ backgroundColor: 'var(--color-background-lighter, #f8f9fa)', marginBottom: '2rem' }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
              <AlertTriangle size={20} style={{ color: 'var(--color-primary)', flexShrink: 0, marginTop: '2px' }} />
              <div style={{ fontSize: '0.9rem', lineHeight: '1.5' }}>
                <ul style={{ margin: '0', paddingLeft: '1.25rem' }}>
                  <li>Minimum 1 parent required, maximum 2 parents allowed</li>
                  <li>At least 1 player must be registered</li>
                  <li>At least 1 emergency contact is required</li>
                  {(!hasParents || !hasPlayers || !hasEmergencyContact) && (
                    <li style={{ color: 'var(--color-error, #dc3545)', fontWeight: 'bold' }}>
                      ⚠ Please complete all required sections
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </ProfileInfoCard>
        )}

        {/* Account Information */}
        <ProfileInfoCard
          title="Account Information"
          variant="contrast"
        >
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1rem'
          }}>
            <div>
              <strong>Email:</strong> {user?.email}
            </div>
            <div>
              <strong>Status:</strong> {user?.account_status?.replace('_', ' ')?.toUpperCase()}
            </div>
            <div>
              <strong>Member Since:</strong> {user?.created_at ? new Date(user.created_at).toLocaleDateString() : ''}
            </div>
            <div>
              <strong>Last Login:</strong> {user?.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
            </div>
          </div>
        </ProfileInfoCard>

      {/* Parents Section */}
      <ProfileInfoCard
        title={`Parents ${parentsCount > 0 ? `(${parentsCount} / 2 max)` : ''}`}
      >
        {!user?.verification?.verified_by && canAddParent && (
          <div style={{ marginBottom: '1rem', textAlign: 'right' }}>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAddingParent(true)}
              disabled={saving || !canAddParent}
              title={!canAddParent ? 'Maximum of 2 parents allowed' : 'Add a parent'}
            >
              <Plus size={16} /> Add Parent
            </Button>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>
              Loading parent information...
            </p>
          </div>
        ) : null}

        {hasParents ? (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {familyData.parents.map((parent, index) => (
              <div key={parent.id} style={{
                background: 'var(--color-background-lighter, #f9f9f9)',
                padding: '1.25rem',
                borderRadius: '8px',
                border: '1px solid var(--color-border, #e0e0e0)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <h4 style={{ color: 'var(--color-primary)', fontSize: '1.1rem', margin: '0' }}>
                    {parent.first_name} {parent.last_name}
                    {parent.emergency_contact && (
                      <span style={{
                        marginLeft: '0.5rem',
                        fontSize: '0.85rem',
                        color: 'var(--color-text-secondary)'
                      }}>
                        (Emergency Contact)
                      </span>
                    )}
                  </h4>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Badge variant="success" size="sm">
                      Parent {index + 1}
                    </Badge>
                    {!user?.verification?.verified_by && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingParent(parent)}
                        disabled={saving}
                      >
                        <Edit size={16} /> Edit
                      </Button>
                    )}
                  </div>
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem',
                  fontSize: '0.9rem',
                  marginTop: '1rem'
                }}>
                  <div>
                    <strong>Email:</strong> {parent.email || 'Not provided'}
                  </div>
                  <div>
                    <strong>Phone:</strong> {parent.phone || 'Not provided'}
                  </div>
                  <div>
                    <strong>Relationship:</strong> {parent.relationship_to_player || 'Not specified'}
                  </div>
                  <div>
                    <strong>Address:</strong> {parent.address?.address_line1 ? `${parent.address.address_line1}${parent.address.address_line2 ? ', ' + parent.address.address_line2 : ''}, ${parent.address.city}, ${parent.address.state} ${parent.address.zip_code}` : 'Not provided'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem', background: 'var(--color-warning-light, #fff3cd)', borderRadius: '8px' }}>
            <h4 style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-warning, #856404)' }}>
              <AlertTriangle size={18} style={{ marginRight: '0.5rem' }} /> No Parent Information Found
            </h4>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '1rem', marginBottom: '1rem' }}>
              Please complete your registration to add parent information.
            </p>
            <Button variant="primary" onClick={() => navigate('/register')}>
              Complete Registration
            </Button>
          </div>
        )}
      </ProfileInfoCard>

      {/* Players Section */}
      <ProfileInfoCard
        title="Players"
      >
        {!user?.verification?.verified_by && (
          <div style={{ marginBottom: '1rem', textAlign: 'right' }}>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setAddingPlayer(true)}
              disabled={saving}
            >
              <Plus size={16} /> Add Player
            </Button>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '1rem', margin: '0' }}>
              Loading player information...
            </p>
          </div>
        ) : null}

        {hasPlayers ? (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {familyData.players.map((player, index) => (
              <div key={player.id} style={{
                background: 'var(--color-background-lighter, #f9f9f9)',
                padding: '1.25rem',
                borderRadius: '8px',
                border: '1px solid var(--color-border, #e0e0e0)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <h4 style={{ color: 'var(--color-primary)', fontSize: '1.1rem', margin: '0' }}>
                    {player.first_name} {player.last_name}
                  </h4>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Badge variant="success" size="sm">
                      Player {index + 1}
                    </Badge>
                    {!user?.verification?.verified_by && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingPlayer(player)}
                        disabled={saving}
                      >
                        <Edit size={16} /> Edit
                      </Button>
                    )}
                  </div>
                </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '1rem',
                      fontSize: '0.9rem',
                      marginTop: '1rem'
                    }}>
                      <div>
                        <strong>Date of Birth:</strong> {player.date_of_birth ? new Date(player.date_of_birth).toLocaleDateString() : 'Not provided'}
                      </div>
                      <div>
                        <strong>Gender:</strong> {player.gender ? player.gender.charAt(0).toUpperCase() + player.gender.slice(1) : 'Not specified'}
                      </div>
                      <div>
                        <strong>School:</strong> {player.school || 'Not provided'}
                      </div>
                      <div>
                        <strong>Grade:</strong> {player.grade || 'Not provided'}
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <strong>Emergency Contact:</strong> {player.emergency_contactName || 'Not provided'}
                        {player.emergency_contactPhone && ` (${player.emergency_contactPhone})`}
                      </div>
                      {player.previous_experience && (
                        <div style={{ gridColumn: '1 / -1' }}>
                          <strong>Soccer Experience:</strong> {player.previous_experience}
                        </div>
                      )}
                      {player.medical_conditions && (
                        <div style={{ gridColumn: '1 / -1' }}>
                          <strong>Medical Notes:</strong> {player.medical_conditions}
                        </div>
                      )}
                    </div>

                {/* Skills Assessment Link */}
                <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--color-border, #e0e0e0)' }}>
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/skills/${player.id}`)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <polygon points="16.24,7.76 14.12,14.12 7.76,16.24 9.88,9.88" />
                    </svg>
                    View Skills Assessment
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : registrationComplete ? (
          <div style={{ textAlign: 'center', padding: '2rem', background: 'var(--color-success-light, #d4edda)', borderRadius: '8px' }}>
            <h4 style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-primary)' }}>
              <Icon iconNode={soccerBall} size={18} style={{ marginRight: '0.5rem' }} /> Ready to Add Players!
            </h4>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '1rem', marginBottom: '1rem' }}>
              Your registration is complete. You can now add players to enroll in programs.
            </p>
            <Button variant="primary">
              Add First Player
            </Button>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem', background: 'var(--color-warning-light, #fff3cd)', borderRadius: '8px' }}>
            <h4 style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-warning, #856404)' }}>
              <AlertTriangle size={18} style={{ marginRight: '0.5rem' }} /> Player Registration Required
            </h4>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '1rem', marginBottom: '1rem' }}>
              Complete your registration to add your first player and access training programs.
            </p>
            <Button variant="primary" onClick={() => navigate('/register')}>
              Register First Player
            </Button>
          </div>
        )}
      </ProfileInfoCard>

      {/* Emergency Contacts Section */}
      <ProfileInfoCard
        title="Emergency Contacts"
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '1rem', margin: '0' }}>
              Loading emergency contact information...
            </p>
          </div>
        ) : (
          (() => {
            const primary = familyData?.parents?.find(p => p.family_role === 'primary_parent');
            const emergencyContacts = primary?.emergency_contacts || [];
            const hasContacts = emergencyContacts.length > 0;

            return (
              <>
                {!user?.verification?.verified_by && (
                  <div style={{ marginBottom: '1rem', textAlign: 'right' }}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingEmergencyContacts(true)}
                      disabled={saving}
                    >
                      {hasContacts ? (
                        <><Edit size={16} /> Edit Contacts</>
                      ) : (
                        <><Plus size={16} /> Add Contacts</>
                      )}
                    </Button>
                  </div>
                )}

                {hasContacts ? (
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    {emergencyContacts.map((contact, index) => (
                      <div key={index} style={{
                        background: 'var(--color-background-lighter, #f9f9f9)',
                        padding: '1.25rem',
                        borderRadius: '8px',
                        border: '1px solid var(--color-border, #e0e0e0)'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                          <h4 style={{ color: 'var(--color-primary)', fontSize: '1.1rem', margin: '0' }}>
                            {contact.type === 'user' ? (
                              (() => {
                                const parent = familyData.parents.find(p => p.id === contact.user_id);
                                return parent ? `${parent.first_name} ${parent.last_name}` : 'Parent';
                              })()
                            ) : (
                              contact.name
                            )}
                          </h4>
                          <Badge variant="success" size="sm">
                            Contact {index + 1}
                          </Badge>
                        </div>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                          gap: '1rem',
                          fontSize: '0.9rem'
                        }}>
                          {contact.type === 'user' ? (
                            (() => {
                              const parent = familyData.parents.find(p => p.id === contact.user_id);
                              return parent ? (
                                <>
                                  <div>
                                    <strong>Phone:</strong> {parent.phone || 'Not provided'}
                                  </div>
                                  <div>
                                    <strong>Email:</strong> {parent.email || 'Not provided'}
                                  </div>
                                  <div>
                                    <strong>Type:</strong> Family Contact
                                  </div>
                                </>
                              ) : null;
                            })()
                          ) : (
                            <>
                              <div>
                                <strong>Phone:</strong> {contact.phone || 'Not provided'}
                              </div>
                              <div>
                                <strong>Relationship:</strong> {contact.relationship || 'Not specified'}
                              </div>
                              <div>
                                <strong>Type:</strong> External Contact
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '2rem', background: 'var(--color-warning-light, #fff3cd)', borderRadius: '8px' }}>
                    <h4 style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-warning, #856404)' }}>
                      <AlertTriangle size={18} style={{ marginRight: '0.5rem' }} /> No Emergency Contacts
                    </h4>
                    <p style={{ color: 'var(--color-text-secondary)', fontSize: '1rem', marginBottom: '1rem' }}>
                      Add at least one emergency contact for safety and communication purposes.
                    </p>
                    {!user?.verification?.verified_by && (
                      <Button variant="primary" onClick={() => setEditingEmergencyContacts(true)}>
                        Add Emergency Contacts
                      </Button>
                    )}
                  </div>
                )}
              </>
            );
          })()
        )}
      </ProfileInfoCard>

      {/* Profile Management Actions */}
      {!registrationComplete && (
        <ProfileInfoCard
          title="Profile Actions"
        >
          <div style={{ textAlign: 'center' }}>
            <Button variant="primary" onClick={() => navigate('/register')} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
              <Edit size={18} /> Complete Registration
            </Button>
          </div>
        </ProfileInfoCard>
      )}
      </div>

      {/* Add Parent Modal */}
      <Modal
        isOpen={addingParent}
        onClose={() => setAddingParent(false)}
        title="Add Parent"
        size="large"
      >
        <ParentEditForm
          parent={null}
          onSave={handleAddParent}
          onCancel={() => setAddingParent(false)}
        />
      </Modal>

      {/* Edit Parent Modal */}
      <Modal
        isOpen={!!editingParent}
        onClose={() => setEditingParent(null)}
        title="Edit Parent Information"
        size="large"
      >
        {editingParent && (
          <ParentEditForm
            parent={editingParent}
            onSave={handleSaveParent}
            onCancel={() => setEditingParent(null)}
          />
        )}
      </Modal>

      {/* Add Player Modal */}
      <Modal
        isOpen={addingPlayer}
        onClose={() => setAddingPlayer(false)}
        title="Add Player"
        size="large"
      >
        <PlayerEditForm
          player={null}
          isEnrolled={false}
          onSave={handleAddPlayer}
          onCancel={() => setAddingPlayer(false)}
        />
      </Modal>

      {/* Edit Player Modal */}
      <Modal
        isOpen={!!editingPlayer}
        onClose={() => setEditingPlayer(null)}
        title="Edit Player Information"
        size="large"
      >
        {editingPlayer && (
          <PlayerEditForm
            player={editingPlayer}
            isEnrolled={editingPlayer.isEnrolled || false}
            onSave={handleSavePlayer}
            onCancel={() => setEditingPlayer(null)}
          />
        )}
      </Modal>

      {/* Emergency Contacts Modal */}
      <Modal
        isOpen={editingEmergencyContacts}
        onClose={() => setEditingEmergencyContacts(false)}
        title="Emergency Contacts"
        size="large"
      >
        <EmergencyContactEditor
          parents={familyData?.parents || []}
          existingContacts={(() => {
            const primary = familyData?.parents?.find(p => p.family_role === 'primary_parent');
            return primary?.emergency_contacts || [];
          })()}
          onSave={handleEmergencyContactsSave}
          onCancel={() => setEditingEmergencyContacts(false)}
          readOnly={false}
        />
      </Modal>
    </PageLayout>
  );
};

// Main Profile Component with Role-based Rendering
const Profile = () => {
  const { isPlatformAdmin, isClubAdmin, isBranchAdmin, isCoach, isParent, isPlayer, userRole, isAuthenticated, loading } = useRole();
  const { user } = useAuth();

  // Debug logging to understand what's happening
  console.log('Profile Debug:', {
    userRole,
    isPlatformAdmin,
    isClubAdmin,
    isBranchAdmin,

    isCoach,
    isParent,
    isPlayer,
    userFromAuth: user?.primary_role,
    isAuthenticated,
    loading
  });

  // Show loading while auth is being checked
  if (loading) {
    return (
      <PageLayout>
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '1rem', margin: 0 }}>Loading profile...</p>
        </div>
      </PageLayout>
    );
  }


  // Platform admin gets minimal admin profile
  if (isPlatformAdmin) {
    return <PlatformAdminProfile />;
  }

  // Club admin gets club admin profile
  if (isClubAdmin) {
    return <ClubAdminProfile />;
  }

  // Branch admin gets branch admin profile
  if (isBranchAdmin) {
    return <BranchAdminProfile />;
  }

  // Coach gets coach-specific profile (no parent/player sections)
  if (isCoach) {
    return <CoachProfile />;
  }

  // Parents get the full family profile with parent/player/emergency contact sections
  if (isParent) {
    return <ParentProfile />;
  }

  // Players get their own simple profile (for now, same as parent but could be customized)
  if (isPlayer) {
    return <ParentProfile />;
  }

  // Special fallback for users with unknown roles - assume they're parents if authenticated
  // This handles cases where the role might not be properly detected
  if (isAuthenticated && user) {
    console.warn('Unknown role detected, falling back to ParentProfile:', userRole);
    return <ParentProfile />;
  }

  // Fallback for unknown roles
  return (
    <PageLayout>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '2rem 1rem'
      }}>
        <div style={{
          marginBottom: '2rem',
          paddingBottom: '1.5rem',
          borderBottom: '2px solid var(--color-primary)'
        }}>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'var(--color-primary)',
            margin: '0 0 0.5rem 0'
          }}>PROFILE</h1>
          <p style={{
            fontSize: '1rem',
            color: 'var(--color-text-secondary)',
            margin: 0
          }}>User profile information</p>
        </div>
        <Card variant="transparent">
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <h3 style={{ color: 'var(--primary-green)', marginBottom: '1rem' }}>Profile Not Available</h3>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '1rem', marginBottom: '1rem' }}>
              Profile view not configured for role: {userRole}
            </p>
            <p style={{ color: 'var(--color-text-tertiary)', fontSize: '0.8rem', margin: 0 }}>
              Debug: isPlatformAdmin={isPlatformAdmin?.toString()}, isClubAdmin={isClubAdmin?.toString()},
              isCoach={isCoach?.toString()}, isParent={isParent?.toString()}, isPlayer={isPlayer?.toString()}
            </p>
          </div>
        </Card>
      </div>
    </PageLayout>
  );
};

export default Profile;
